import React, {useState} from 'react'
import ftmlogo from '../../assets/images/fresh-thyme-market-logo.png';
import {SummaryModal} from "../modals/SummaryModal";
import {getHandleBlur} from "../../utils/dateValidator";
import DynamicAlert from "../../helpers/dynamicAlert";
import "./Update.css";
import Employment from "../../pages/admin/Employment";
import Subscriptions from "../../pages/admin/Subscriptions";
import AdminActions from "../../pages/admin/AdminActions";
import UpdatePI from "../../pages/admin/UpdatePI";
import PropTypes from "prop-types";
import UserMenu from "./UserMenu";
import StatusBanners from "./StatusBanners";
import LookupSearch from "./LookupSearch";
import AdminTabsNav from "./AdminTabsNav";
import DetailsTab from "./DetailsTab";
import {TABS, getEmployeeWarnings, getSuppressionTab, tabPaneClass} from "./adminUpdateUtils";
import {useAccountActions, useBlockStatus, useDefaultStore, useSuppressionStatus} from "./adminUpdateHooks";

const pageStyle = {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    paddingTop: "40px",
    boxSizing: "border-box",
};

const cardStyle = {
    width: "100%",
    maxWidth: "1500px",
    boxSizing: "border-box",
    margin: "0",
    padding: "40px",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
};

// Details tab holds the wide comparison table, so let the card use the full screen width there.
const detailsCardStyle = {maxWidth: "120%", padding: "32px 16px"};

const fullWidthStyle = {width: "100%", maxWidth: "100%", boxSizing: "border-box"};

export const AdminUpdate = (props) => {
    const {
        handleSubmit,
        handleChange,
        error,
        stores,
        changeStoreLocation,
        showSummary,
        registering,
        alert,
        lookingUp,
        lookupData,
        setLookupData,
        lookupError,
        handleLookup,
        lookUpResponse,
        user,
        handleSummaryClose,
        autoSet,
        showAlert,
        setLookUpResponse,
        userName,
        employeeStatusList,
        auth0Details,
        userImage,
        cosmosCustomer,
        comparisonSummary
    } = props;

    const [activeTab, setActiveTab] = useState(TABS.DETAILS);

    const {suppressionStatus, isUnsuppressing, handleUnsuppress} = useSuppressionStatus({
        lookUpResponse,
        lookupData,
        cosmosCustomer,
        showAlert,
        onSuppressed: reasons => setActiveTab(getSuppressionTab(reasons))
    });

    const {blockStatus, isUnblocking, handleUnblockAccount} = useBlockStatus({
        lookUpResponse,
        customerId: user?.customerId,
        showAlert
    });

    const defaultStore = useDefaultStore({
        storeNumber: cosmosCustomer?.storeNumber,
        stores,
        autoSet,
        changeStoreLocation
    });

    const {
        handleDeleteAccount,
        handleResetPasswordLink,
        handleVerifyEmailLink,
        handleUpdateWynshopLoyaltyId
    } = useAccountActions({user, lookUpResponse, showAlert, setLookUpResponse});

    const handleStoreChange = (values) => {
        if (!values?.length) {
            return;
        }
        const [store] = values;
        changeStoreLocation({target: {name: "storeLocation", value: store.originalEcomName}});
        changeStoreLocation({target: {name: "storeNumber", value: store.storeNumber}});
    };

    const handleBlur = (event) => {
        if (event.target.value.length >= 11) {
            return;
        }
        const value = getHandleBlur(event);
        if (value) {
            handleChange({target: {name: 'birthday', value}});
        }
    };

    const showDetails = {comparisonDetails: lookUpResponse?.meta?.comparisonSummary, showSummary, handleSummaryClose};
    const hasEmail = Boolean(user?.email);
    const hasDeleteAccess = Boolean(lookUpResponse?.meta?.hasDeleteAccess);

    return (
        <div style={pageStyle}>
            <div style={activeTab === TABS.DETAILS ? {...cardStyle, ...detailsCardStyle} : cardStyle}>
                <div style={{textAlign: "center"}}>
                    <img src={ftmlogo} alt="Fresh Thyme Market logo" width="180"/>
                </div>

                <div style={{display: "flex", justifyContent: "flex-end", marginBottom: "2px"}}>
                    {userName && <UserMenu userName={userName} userImage={userImage}/>}
                </div>

                <form>
                    <DynamicAlert alert={alert}/>

                    <StatusBanners blockStatus={blockStatus}
                                   suppressionStatus={suppressionStatus}
                                   onTabClick={setActiveTab}/>

                    <LookupSearch lookupData={lookupData}
                                  setLookupData={setLookupData}
                                  lookupError={lookupError}
                                  lookingUp={lookingUp}
                                  handleLookup={handleLookup}
                                  lookUpResponse={lookUpResponse}
                                  setLookUpResponse={setLookUpResponse}/>
                </form>

                {showSummary && <SummaryModal {...showDetails} />}

                {lookUpResponse?.data && (
                    <div className="mt-4" style={fullWidthStyle}>
                        <AdminTabsNav activeTab={activeTab}
                                      onSelect={setActiveTab}
                                      showAdminLink={hasEmail}
                                      showRestricted={hasEmail && hasDeleteAccess}/>

                        <div className="tab-content mt-3" style={{...fullWidthStyle, overflowX: "auto"}}>
                            <div className={tabPaneClass(activeTab, TABS.DETAILS)}>
                                <DetailsTab lookUpResponse={lookUpResponse}
                                            user={user}
                                            auth0Details={auth0Details}
                                            showAlert={showAlert}
                                            handleUpdateWynshopLoyaltyId={handleUpdateWynshopLoyaltyId}/>
                            </div>

                            <div className={tabPaneClass(activeTab, TABS.UPDATE)}>
                                <UpdatePI registering={registering}
                                          handleSubmit={handleSubmit}
                                          error={error}
                                          cosmosCustomer={cosmosCustomer}
                                          handleChange={handleChange}
                                          defaultStore={defaultStore}
                                          stores={stores}
                                          comparisonSummary={comparisonSummary}
                                          handleBlur={handleBlur}
                                          handleStoreChange={handleStoreChange}/>
                            </div>

                            <div className={tabPaneClass(activeTab, TABS.PREFERENCES)}>
                                <Subscriptions lookupData={lookupData}
                                               profileId={lookUpResponse?.meta?.klaviyoProfiles?.[0]?.profileId}
                                               showAlert={showAlert}
                                               isActive={activeTab === TABS.PREFERENCES}/>
                            </div>

                            {employeeStatusList?.length > 0 && (
                                <div className={tabPaneClass(activeTab, TABS.EMPLOYMENT)}>
                                    <Employment employeeStatusList={employeeStatusList}
                                                warnings={getEmployeeWarnings(employeeStatusList[0])}/>
                                </div>
                            )}

                            {hasEmail && (
                                <div className={tabPaneClass(activeTab, TABS.ADMIN)}>
                                    <AdminActions handleVerifyEmailLink={handleVerifyEmailLink}
                                                  handleResetPasswordLink={handleResetPasswordLink}
                                                  auth0Details={auth0Details}
                                                  user={user}
                                                  handleDeleteAccount={handleDeleteAccount}
                                                  registering={registering}
                                                  hasDeleteAccess={lookUpResponse?.meta?.hasDeleteAccess}
                                                  suppressionStatus={suppressionStatus}
                                                  handleUnsuppress={handleUnsuppress}
                                                  isUnsuppressing={isUnsuppressing}
                                                  blockStatus={blockStatus}
                                                  handleUnblockAccount={handleUnblockAccount}
                                                  isUnblocking={isUnblocking}/>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

AdminUpdate.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    error: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    stores: PropTypes.arrayOf(
        PropTypes.shape({
            storeNumber: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number
            ]),
            originalEcomName: PropTypes.string
        })
    ),
    changeStoreLocation: PropTypes.func,
    showSummary: PropTypes.bool,
    registering: PropTypes.bool,
    alert: PropTypes.shape({
        id: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        type: PropTypes.string,
        msg: PropTypes.string
    }),
    lookingUp: PropTypes.bool,
    lookupData: PropTypes.shape({
        fieldType: PropTypes.string,
        searchValue: PropTypes.string
    }),
    setLookupData: PropTypes.func.isRequired,
    lookupError: PropTypes.object,
    handleLookup: PropTypes.func,
    lookUpResponse: PropTypes.shape({
        status: PropTypes.string,
        message: PropTypes.string,
        data: PropTypes.array,
        meta: PropTypes.object
    }),
    user: PropTypes.object,
    handleSummaryClose: PropTypes.func,
    autoSet: PropTypes.bool,
    showAlert: PropTypes.func.isRequired,
    setLookUpResponse: PropTypes.func.isRequired,
    userName: PropTypes.string,
    employeeStatusList: PropTypes.array,
    auth0Details: PropTypes.object,
    userImage: PropTypes.string,
    cosmosCustomer: PropTypes.object,
    comparisonSummary: PropTypes.object
};

AdminUpdate.defaultProps = {
    error: null,
    stores: [],
    changeStoreLocation: () => {},
    showSummary: false,
    registering: false,
    alert: null,
    lookingUp: false,
    lookupData: {
        fieldType: "PHONE_NO",
        searchValue: ""
    },
    lookupError: {},
    handleLookup: undefined,
    lookUpResponse: null,
    user: null,
    handleSummaryClose: undefined,
    autoSet: false,
    userName: "",
    employeeStatusList: [],
    auth0Details: null,
    userImage: "",
    cosmosCustomer: null,
    comparisonSummary: null
};
